<template>
  <div class="project-container">
    <!-- 顶部banner -->
    <div class="project-banner">
      <img src="@/assets/topbanner.jpg" alt="工程案例" class="banner-img">
      <div class="banner-text">工程案例</div>
    </div>

    <!-- 主体内容 -->
    <div class="project-content">
      <!-- 标签页切换 -->
      <div class="tabs">
        <div 
          :class="['tab', { active: activeTab === 'all' }]"
          @click="activeTab = 'all'; currentPage = 1"
        >
          所有工程
        </div>
        <div 
          :class="['tab', { active: activeTab === 'transmission' }]"
          @click="activeTab = 'transmission'; currentPage = 1"
        >
          输电工程
        </div>
        <div 
          :class="['tab', { active: activeTab === 'distribution' }]"
          @click="activeTab = 'distribution'; currentPage = 1"
        >
          配电工程
        </div>
        <div 
          :class="['tab', { active: activeTab === 'newenergy' }]"
          @click="activeTab = 'newenergy'; currentPage = 1"
        >
          新能源工程
        </div>
        <div 
          :class="['tab', { active: activeTab === 'maintenance' }]"
          @click="activeTab = 'maintenance'; currentPage = 1"
        >
          土建工程
        </div>
      </div>

      <!-- 项目网格 -->
      <div class="project-grid">
        <div 
          v-for="project in currentProjects" 
          :key="project._id"
          class="project-item"
          @click="viewProjectDetail(project._id)"
        >
          <div class="project-image">
            <img :src="project.cover[0]" :alt="project.title" class="project-img">
            <div class="project-tag">{{ getProjectType(project.category) }}</div>
          </div>
          <div class="project-info">
            <h3 class="project-title">{{ project.title }}</h3>
            <div class="project-meta">
              <p class="meta-item"><span>建设单位：</span>{{ project.company || '未知' }}</p>
              <p class="meta-item"><span>工程地址：</span>{{ project.location || '未知' }}</p>
              <p class="meta-item"><span>工程规模：</span>{{ project.scale || '未知' }}</p>
              <p class="meta-item"><span>投资额：</span>{{ project.investment || '未知' }}</p>
            </div>
            <div class="view-detail">查看详情 ></div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <button 
          :disabled="currentPage === 1"
          @click="currentPage--"
          class="page-btn"
        >
          上一页
        </button>
        <span class="page-info">第 {{ currentPage }} 页</span>
        <button 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
          class="page-btn"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'

// SEO Meta标签配置
useHead({
  title: '工程案例 - 华洲电力建设有限公司',
  meta: [
    {
      name: 'description',
      content: '华洲电力建设有限公司工程案例展示，包括输电工程、配电工程、新能源工程和土建工程等各类电力工程项目。'
    },
    {
      name: 'keywords',
      content: '华洲电力,工程案例,输电工程,配电工程,新能源工程,土建工程'
    },
    {
      name: 'author',
      content: '华洲电力建设有限公司'
    },
    {
      property: 'og:title',
      content: '工程案例 - 华洲电力建设有限公司'
    },
    {
      property: 'og:description',
      content: '华洲电力建设有限公司工程案例展示，包括输电工程、配电工程、新能源工程和土建工程等各类电力工程项目。'
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})

const router = useRouter()
const projectList = ref([])

// 标签页状态
const activeTab = ref('all')
// 分页状态
const currentPage = ref(1)
const pageSize = 6

// 项目类型映射
const categoryMap = {
  'transmission': { label: '输电工程', value: 1 },
  'distribution': { label: '配电工程', value: 2 },
  'newenergy': { label: '新能源工程', value: 3 },
  'maintenance': { label: '土建工程', value: 4 }
}

// 获取项目类型标签
const getProjectType = (category) => {
  for (const key in categoryMap) {
    if (categoryMap[key].value === category) {
      return categoryMap[key].label
    }
  }
  return '其他工程'
}

// 计算当前显示的项目列表
const currentProjects = computed(() => {
  let filteredList = projectList.value
  
  // 根据选中的标签页过滤
  if (activeTab.value !== 'all') {
    const category = categoryMap[activeTab.value].value
    filteredList = filteredList.filter(project => project.category === category)
  }
  
  // 分页
  const start = (currentPage.value - 1) * pageSize
  const end = start + pageSize
  return filteredList.slice(start, end)
})

// 计算总页数
const totalPages = computed(() => {
  let filteredList = projectList.value
  
  // 根据选中的标签页过滤
  if (activeTab.value !== 'all') {
    const category = categoryMap[activeTab.value].value
    filteredList = filteredList.filter(project => project.category === category)
  }
  
  return Math.ceil(filteredList.length / pageSize)
})

// 查看项目详情
const viewProjectDetail = (id) => {
  console.log('尝试查看项目详情，ID:', id)
  // 查找对应的项目对象
  const project = projectList.value.find(p => p._id === id)
  
  if (project) {
    console.log('找到项目数据:', project)
    // 主要使用sessionStorage传递数据，这是最可靠的方式
    try {
      // 1. 存储到sessionStorage作为主要数据传递方式
      const storageKey = `project_${id}`
      sessionStorage.setItem(storageKey, JSON.stringify(project))
      console.log('✓ 项目数据已保存到sessionStorage')
      
      // 2. 同时创建全局缓存作为备用方案
      if (!window.__projectDataCache) {
        window.__projectDataCache = {}
      }
      window.__projectDataCache[id] = project
      console.log('✓ 项目数据已保存到全局缓存')
      
      // 3. 简单的路由跳转，不需要状态参数
      router.push(`/project/${id}`)
      
    } catch (error) {
      console.error('传递项目数据时出错:', error)
      // 即使出错也尝试导航，目标页面会使用回退数据
      router.push(`/project/${id}`)
    }
  } else {
    console.warn('未找到ID为', id, '的项目，将使用默认数据')
    // 即使找不到项目也进行导航，目标页面会使用回退数据
    router.push(`/project/${id}`)
  }
}

// 获取项目数据
const fetchProjects = async () => {
  // try {
  //   const res = await axios.get('/webapi/product/list')
  //   console.log('项目数据:', res.data.data)
  //   projectList.value = res.data.data
  // } catch (error) {
  //   console.error('获取项目列表失败:', error)
    // 如果API调用失败，使用模拟数据
    projectList.value = [
      {
        _id: '1',
        title: '城西产业园区110kV变电站新建工程专业分包',
        cover: [require('@/assets/anli1.jpg'),require('@/assets/anli11.jpg'),require('@/assets/anli12.jpg')],
        category: 4,
        company: '江西省虔安电力有限公司变电分公司',
        location: '江西省赣州市',
        scale: '110kV变电站新建工程',
        investment: '约446万元'
      },
      {
        _id: '2',
        title: '肇庆峡顶项目（一期）外电接入工程',
        cover: [require('@/assets/anli2.jpg'),require('@/assets/anli21.jpg'),require('@/assets/anli22.jpg'),require('@/assets/anli23.jpg')],
        category: 1,
        company: '肇庆学院',
        location: '肇庆学院主校区以北，北岭二路以南，学院路以西',
        scale: '配电工程',
        investment: '约619万元'
      },
      {
        _id: '3',
        title: '广东华电韶关曲江樟市光伏发电项目二期35kV集电线路、箱变安装工程',
        cover: [require('@/assets/anli3.jpg'),require('@/assets/anli31.jpg'),require('@/assets/anli32.jpg'),require('@/assets/anli33.jpg')],
        category: 3,
        company: '肇庆学院',
        location: '肇庆学院主校区以北，北岭二路以南，学院路以西',
        scale: '35kV集电线路工程',
        investment: '约619万元'
      },
      {
        _id: '4',
        title: '湖南联塑科技实业有限公司光伏发电EPC设计-施工总承包项目',
        cover: [require('@/assets/anli4.jpg')],
        category: 3,
        company: '中建五局第三建设有限公司宁乡分公司',
        location: '宁乡市永佳路联塑一期、二期厂房内 ',
        scale: '光伏发电EPC工程',
        investment: '约144万元'
      },
      {
        _id: '5',
        title: '华能克州阿合奇县30万千瓦风电项目',
        cover: [require('@/assets/anli5.jpg'),require('@/assets/anli51.jpg'),require('@/assets/anli52.jpg'),require('@/assets/anli53.jpg')],
        category: 3,
        company: '华能国际工程技术有限公司',
        location: '新疆',
        scale: '30万千瓦风电项目',
        investment: '约939万元'
      },
      {
        _id: '6',
        title: '华能山东分公司聊城冠县整县分布式光伏灵芝科技园 7MW 项目 EPC 工程',
        cover: [require('@/assets/anli6.jpg'),require('@/assets/anli61.jpg'),require('@/assets/anli62.jpg'),require('@/assets/anli63.jpg'),require('@/assets/anli64.jpg')],
        category: 3,
        company: '华能(聊城冠县)新能源开发有限公司',
        location: '聊城市冠县店子镇国兴集团灵芝科技示范园',
        scale: '分布式光伏项目',
        investment: '约13000万元'
      },
      {
        _id: '7',
        title: '宁乡经开区美妆谷一期项目设计采购施工总承包强电工程专业分包合同',
        cover: [require('@/assets/anli7.jpg')],
        category: 2,
        company: '中建五局第三建设有限公司宁乡分公司',
        location: '湖南省宁乡市',
        scale: '强电工程专业分包',
        investment: '约2988万元'
      },
      {
        _id: '8',
        title: '万安县整县屋顶分布式光伏开发试点项目采购安装合同',
        cover: [require('@/assets/anli8.jpg'),require('@/assets/anli81.jpg'),require('@/assets/anli82.jpg')],
        category: 3,
        company: '万安县发展和改革委员会',
        location: '江西省吉安市万安县',
        scale: '屋顶分布式光伏开发',
        investment: '约700万元'
      },
      {
        _id: '9',
        title: '兴和县赛乌素镇6MWp光伏帮扶工程村级电站项目EPC总承包合同',
        cover: [require('@/assets/anli9.jpg'),require('@/assets/anli91.jpg'),require('@/assets/anli92.jpg'),require('@/assets/anli93.jpg')],
        category: 3,
        company: '兴和县泰阳能源投资开发有限公司',
        location: '兴和县赛乌素镇',
        scale: '6MWp光伏帮扶工程',
        investment: '约1700万元'
      },
      {
        _id: '10',
        title: '永兴县两新产业园110kV变电站土建及电气安装等工程施工合同',
        cover: [require('@/assets/anli10.jpg'),require('@/assets/anli101.jpg')],
        category: 4,
        company: '湖南郴电国际发展股份有限公司',
        location: '湖南省郴州市永兴县',
        scale: '110kV变电站土建及电气安装',
        investment: '约936万元'
      }
    ]
  // }
}

// 组件挂载时获取数据
onMounted(() => {
  fetchProjects()
})
</script>

<style lang="scss" scoped>
.project-container {
  width: 100%;
  min-height: 100vh;
}

/* 顶部banner样式 */
.project-banner {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 36px;
  font-weight: bold;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

/* 主体内容样式 */
.project-content {
  width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

/* 标签页样式 */
.tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.tab {
  padding: 8px 24px;
  margin: 0 10px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  border-radius: 20px;
  transition: all 0.3s ease;
  background: #f0f0f0;
}

.tab.active {
  color: #fff;
  background: #3f5ff0;
  font-weight: 600;
}

.tab:hover:not(.active) {
  background: #a9b7f4;
  color: #3f5ff0;
}

/* 项目网格样式 */
.project-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 40px;
}

.project-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.project-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-5px);
}

.project-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.project-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.project-item:hover .project-img {
  transform: scale(1.05);
}

.project-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 12px;
  background: #3f5ff0;
  color: #fff;
  font-size: 12px;
  border-radius: 4px;
}

.project-info {
  padding: 15px;
}

.project-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.project-meta {
  margin-bottom: 15px;
}

.meta-item {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.5;
}

.meta-item span {
  color: #999;
}

.view-detail {
  font-size: 14px;
  color: #3f5ff0;
  text-align: right;
  font-weight: 500;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.page-btn {
  padding: 8px 20px;
  background: #3f5ff0;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #40a9ff;
}

.page-btn:disabled {
  background: #d9d9d9;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .project-content {
    width: 90%;
  }
  
  .project-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .project-banner {
    height: 200px;
  }
  
  .banner-text {
    font-size: 24px;
  }
  
  .project-grid {
    grid-template-columns: 1fr;
  }
  
  .tabs {
    flex-wrap: wrap;
  }
  
  .tab {
    padding: 6px 16px;
    margin: 5px;
    font-size: 14px;
  }
}
</style>
